/**
 * Client-side trader-signal filters + sorts for P2P Intelligence (P1-P6).
 * Pure helpers — kept out of the page component so Vitest can cover them
 * without mounting the full dashboard.
 */

export type RiskHint =
    | 'sub_30s_release'
    | 'very_short_pay_window'
    | 'restrictive_filters'
    | 'high_cancel_ratio'
    | 'high_recent_tempo'
    | 'low_completion';

export interface TraderSignalsLike {
    kyc?: {
        tier?: string | number | null;
        trust_level?: string | number | null;
        verified_capital?: boolean | null;
    };
    activity?: {
        is_online_now?: boolean | null;
        recent_orders_30d?: number | null;
        cancel_count?: number | null;
        avg_release_time_s?: number | null;
    };
    commercial?: {
        pay_window_s?: number | null;
        has_counterparty_filters?: boolean;
    };
    risk_hints?: RiskHint[] | string[] | null;
}

export type TraderSignalChip =
    | 'kyc_verified'
    | 'kyc_unverified'
    | 'online_now'
    | 'vetted'
    | 'bot_release'
    | 'high_tempo';

export type TraderSignalSort =
    | 'off'
    | 'recent_orders_30d'
    | 'cancel_count'
    | 'pay_window_s';

export interface TraderSignalFilterState {
    chips: Set<TraderSignalChip> | TraderSignalChip[];
    /** When both kyc chips are off, KYC is "any". */
    sort: TraderSignalSort;
    sortDir: 'asc' | 'desc';
}

const KYC_UNVERIFIED = new Set(['', 'user', 'unverified', '0', 'false', 'none', 'null']);

export function isKycVerified(signals?: TraderSignalsLike | null): boolean {
    if (!signals?.kyc) return false;
    if (signals.kyc.verified_capital === true) return true;
    const tier = signals.kyc.tier;
    if (tier == null) return false;
    const s = String(tier).trim().toLowerCase();
    if (!s || KYC_UNVERIFIED.has(s)) return false;
    // merchant / verified / numeric trust tiers count as verified-ish
    return true;
}

export function matchesTraderSignalChips(
    signals: TraderSignalsLike | null | undefined,
    chips: Iterable<TraderSignalChip>,
): boolean {
    const active = chips instanceof Set ? chips : new Set(chips);
    if (active.size === 0) return true;

    const hints = new Set((signals?.risk_hints || []).map(String));

    if (active.has('kyc_verified') && active.has('kyc_unverified')) {
        // contradictory — treat as any (no KYC constraint)
    } else if (active.has('kyc_verified')) {
        if (!isKycVerified(signals)) return false;
    } else if (active.has('kyc_unverified')) {
        if (isKycVerified(signals)) return false;
    }

    if (active.has('online_now') && signals?.activity?.is_online_now !== true) {
        return false;
    }
    if (active.has('vetted')) {
        const vetted =
            signals?.commercial?.has_counterparty_filters === true ||
            hints.has('restrictive_filters');
        if (!vetted) return false;
    }
    if (active.has('bot_release') && !hints.has('sub_30s_release')) {
        return false;
    }
    if (active.has('high_tempo') && !hints.has('high_recent_tempo')) {
        return false;
    }
    return true;
}

function sortValue(
    signals: TraderSignalsLike | null | undefined,
    sort: TraderSignalSort,
): number | null {
    if (!signals || sort === 'off') return null;
    if (sort === 'recent_orders_30d') {
        const v = signals.activity?.recent_orders_30d;
        return typeof v === 'number' ? v : null;
    }
    if (sort === 'cancel_count') {
        const v = signals.activity?.cancel_count;
        return typeof v === 'number' ? v : null;
    }
    if (sort === 'pay_window_s') {
        const v = signals.commercial?.pay_window_s;
        return typeof v === 'number' ? v : null;
    }
    return null;
}

export function applyTraderSignalFiltersAndSort<T extends { trader_signals?: TraderSignalsLike | null }>(
    rows: T[],
    opts: {
        chips?: Iterable<TraderSignalChip>;
        sort?: TraderSignalSort;
        sortDir?: 'asc' | 'desc';
    } = {},
): T[] {
    const chips = opts.chips ?? [];
    let out = rows.filter((r) => matchesTraderSignalChips(r.trader_signals, chips));
    const sort = opts.sort ?? 'off';
    if (sort === 'off') return out;
    const dir = opts.sortDir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
        const va = sortValue(a.trader_signals, sort);
        const vb = sortValue(b.trader_signals, sort);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return (va - vb) * dir;
    });
    return out;
}

export const TRADER_SIGNAL_CHIP_META: Record<
    TraderSignalChip,
    { label: string; hint: string }
> = {
    kyc_verified: { label: 'KYC verified', hint: 'Platform reports a non-basic KYC tier or verified capital' },
    kyc_unverified: { label: 'KYC unverified', hint: 'Missing / basic user tier only' },
    online_now: { label: 'Online now', hint: 'Merchant marked online by the platform' },
    vetted: { label: 'Vetted counterparties', hint: 'Merchant restricts trades by KYC / age / order count' },
    bot_release: { label: 'Bot release', hint: 'Average crypto release under 30s' },
    high_tempo: { label: 'High tempo', hint: 'Over 500 orders in the last 30 days' },
};
